import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  Card,
  SectionHeading,
  Loading,
  ErrorBox,
  Empty,
  HowCalculated,
  DefinitionTable,
  TH,
  TD,
  TD_WRAP,
  fmtDate,
} from '../components/metrics/MetricsUI';
import { fetchAnonymitySettings, saveAnonymitySetting } from '../apis/metrics.api';

/**
 * Anonymity settings. Admin only.
 *
 * One row per organisation, one column per region, one switch per cell — so an
 * organisation can report names in the US and employee numbers in the EU.
 *
 * It was a per-manager preference, which made it a preference rather than a
 * commitment: a manager could put their own organisation's names back on. The
 * route is admin-only now and managers no longer see the page at all.
 */

const REGION_LABEL = { us: 'US', eu: 'EU', ap: 'APAC' };

/** A switch for one (organisation, region) pair. */
function AnonCell({ cell, busy, onToggle }) {
  if (!cell) {
    // Not an error: an organisation legitimately does not exist in every region.
    return <span className="text-xs text-gray-300">not present</span>;
  }

  const on = cell.anonymous;
  return (
    <button
      type="button"
      disabled={busy}
      onClick={() => onToggle(!on)}
      aria-pressed={on}
      title={on ? 'Reporting is anonymised in this region' : 'Reporting shows names in this region'}
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs transition ${
        busy ? 'cursor-wait opacity-50' : 'hover:shadow'
      } ${on ? 'border-navy bg-navy text-white' : 'border-gray-300 bg-white text-gray-600'}`}
    >
      <span className={`h-2 w-2 rounded-full ${on ? 'bg-white' : 'bg-gray-300'}`} />
      {on ? 'Anonymous' : 'Names'}
      {cell.members != null && <span className={on ? 'text-white/70' : 'text-gray-400'}>· {cell.members}</span>}
    </button>
  );
}

export default function MetricsSettings() {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user, shallowEqual);
  const { role } = useSelector((state) => state.user.user, shallowEqual);

  const idToken = user && (user.idToken || user.accessToken);

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busyCell, setBusyCell] = useState(null);
  const [note, setNote] = useState(null);
  const [query, setQuery] = useState('');
  const [onlyAnonymous, setOnlyAnonymous] = useState(false);

  const load = React.useCallback(() => {
    if (!idToken) return;
    setError(null);
    fetchAnonymitySettings(idToken)
      .then(setData)
      .catch((e) => setError(e.message));
  }, [idToken]);

  useEffect(() => {
    load();
  }, [load]);

  /* Columns come from what the server reports, not from a hardcoded list: a
     region that is not deployed in this environment should not get a column. */
  const columns = useMemo(() => {
    if (!data) return [];
    const seen = new Set();
    (data.regions || []).forEach((r) => seen.add(r.region));
    (data.organizations || []).forEach((o) => (o.presence || []).forEach((p) => seen.add(p.region)));
    return ['us', 'eu', 'ap'].filter((r) => seen.has(r));
  }, [data]);

  const rows = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    return (data.organizations || [])
      .filter((o) => (q ? String(o.name || '').toLowerCase().includes(q) : true))
      .filter((o) => (onlyAnonymous ? (o.presence || []).some((p) => p.anonymous) : true));
  }, [data, query, onlyAnonymous]);

  const toggle = async (org, region, next) => {
    /* The id for THIS region. Each region mints its own, so writing the
       headline id into eu would match nothing there and report present:false. */
    const cell = (org.presence || []).find((p) => p.region === region);
    const targetId = (cell && cell.organizationId) || org.organizationId;
    const cellKey = `${org.organizationId}::${region}`;
    setBusyCell(cellKey);
    setNote(null);

    // Optimistic, then reconciled with what the server actually stored — a
    // rejected write must not leave a switch showing a state nobody saved.
    setData((d) => ({
      ...d,
      organizations: d.organizations.map((o) =>
        o.organizationId === org.organizationId
          ? {
              ...o,
              presence: o.presence.map((p) => (p.region === region ? { ...p, anonymous: next } : p)),
            }
          : o
      ),
    }));

    try {
      const result = await saveAnonymitySetting(idToken, {
        organizationId: targetId,
        region,
        anonymous: next,
      });
      if (result && result.present === false) {
        setNote(`${org.name} does not exist in the ${REGION_LABEL[region] || region} region, so nothing was changed.`);
      }
      load();
    } catch (e) {
      setNote(`Could not save: ${e.message}`);
      load();
    } finally {
      setBusyCell(null);
    }
  };

  if (role !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-100 p-6 font-metrics">
        <ErrorBox message="Anonymity settings are managed by LavenirAI staff." onRetry={() => navigate('/metrics')} />
      </div>
    );
  }
  if (!idToken) {
    return (
      <div className="min-h-screen bg-gray-100 p-6 font-metrics">
        <ErrorBox message="Not signed in." onRetry={() => navigate('/login')} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 p-6 font-metrics">
        <ErrorBox message={error} onRetry={load} />
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-gray-100 p-6 font-metrics">
        <Loading what="anonymity settings" />
      </div>
    );
  }

  const failed = (data.regions || []).filter((r) => !r.ok);

  return (
    <div className="min-h-screen bg-gray-100 p-6 font-metrics">
      <div className="mx-auto max-w-[1400px]">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Anonymity settings</h1>
            <p className="text-sm text-gray-500">
              Per organisation, per region. Managers see what is set here; they cannot change it.
            </p>
          </div>
          <button
            onClick={() => navigate('/metrics')}
            className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm hover:shadow"
          >
            ← Metrics
          </button>
        </div>

        {/* A region that did not answer would otherwise look like a region where
            every organisation reports names. */}
        {failed.length > 0 && (
          <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            {failed.map((r) => `${REGION_LABEL[r.region] || r.region} did not answer (${r.error})`).join('. ')}. Its
            settings are not shown and cannot be changed until it is reachable.
          </div>
        )}

        {data.source === 'direct' && (
          <div className="mb-4 rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm text-gray-600">
            Showing the <strong>{REGION_LABEL[columns[0]] || columns[0]}</strong> region only. This server reads one
            database; set <code className="rounded bg-gray-100 px-1">METRICS_VIA_ASSETS</code> to see and change every
            region.
          </div>
        )}

        {note && (
          <div className="mb-4 rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm text-gray-700">{note}</div>
        )}

        <Card>
          <SectionHeading
            title="Organisations"
            subtitle={`${rows.length} of ${(data.organizations || []).length} shown`}
            help={
              <HowCalculated wide>
                <DefinitionTable
                  title="What the switch does"
                  rows={[
                    ['Names', 'Managers and admins see real names and email addresses for that region.'],
                    ['Anonymous', 'Names and addresses are removed before they leave that region’s server, and people appear as Employee 01, 02 and so on.'],
                    ['Numbering', 'Runs across the whole organisation, ordered by when each person joined, so a part-anonymous roster reads as one list and a label does not change between visits.'],
                    ['Drill-down', 'Opening an anonymised person shows the same label, not their name.'],
                    ['Not present', 'The organisation has no record in that region, so there is nothing to set.'],
                  ]}
                />
              </HowCalculated>
            }
            right={
              <div className="flex items-center gap-2">
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search organisations"
                  className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm"
                />
                <label className="flex items-center gap-2 text-sm text-gray-600">
                  <input
                    type="checkbox"
                    checked={onlyAnonymous}
                    onChange={(e) => setOnlyAnonymous(e.target.checked)}
                  />
                  Anonymous only
                </label>
              </div>
            }
          />

          {rows.length === 0 ? (
            <Empty>No organisations match.</Empty>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr>
                    <th className={TH}>Organisation</th>
                    <th className={TH}>Account created</th>
                    <th className={TH}>Members</th>
                    {columns.map((r) => (
                      <th key={r} className={TH}>
                        {REGION_LABEL[r] || r}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map((o) => {
                    const byRegion = new Map((o.presence || []).map((p) => [p.region, p]));
                    return (
                      <tr key={o.organizationId} className="hover:bg-gray-50">
                        <td className={TD_WRAP}>
                          <span className="font-medium text-gray-800">{o.name}</span>
                          {o.mixedAnonymity && (
                            <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[11px] text-amber-800">
                              mixed
                            </span>
                          )}
                        </td>
                        <td className={TD}>{o.createdAt ? fmtDate(o.createdAt) : '—'}</td>
                        <td className={TD}>{o.memberCount}</td>
                        {columns.map((r) => (
                          <td key={r} className={TD}>
                            <AnonCell
                              cell={byRegion.get(r)}
                              busy={busyCell === `${o.organizationId}::${r}`}
                              onToggle={(next) => toggle(o, r, next)}
                            />
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
